/**
 * The response envelopes every NestJS app in `backend/` uses
 * (`@lib/common/responses`). Success bodies come from `DefaultResponse`/
 * `ListResponse`; error bodies come from `HttpExceptionFilter` and carry a
 * different, flatter shape — see `ApiErrorBody` below. Never invent a third
 * shape on the frontend.
 */
export interface DefaultResponseBody<T> {
  data?: T;
  response: {
    status: "success" | "error" | "warning";
    statusCode?: number;
    /** Raw i18n key when `message` is a translated error. */
    key?: string;
    message?: string;
  };
}

export interface ListData<T> {
  list: T[];
  count: number;
  page?: number;
  limit?: number;
  lastPage?: number;
}

export interface ListResponseBody<T> {
  data?: ListData<T>;
  response: {
    status: "success" | "error" | "warning";
    statusCode?: number;
    /** Raw i18n key when `message` is a translated error. */
    key?: string;
    message?: string;
  };
}

/** One invalid field, with every constraint message class-validator raised for it. */
export interface FieldValidationError {
  field: string;
  messages: string[];
}

/**
 * The exact JSON body `HttpExceptionFilter` writes for a non-2xx response:
 * `{ success:false, statusCode, message, key?, data?: { errors } }`.
 */
export interface ApiErrorBody {
  success: false;
  statusCode: number;
  message: string;
  key?: string;
  data?: {
    errors?: FieldValidationError[];
    [key: string]: unknown;
  };
}

/**
 * Normalized shape every API call in the app throws/returns on failure —
 * the frontend skill's "Error Architecture". `fieldErrors` maps a form field
 * name to its messages so a mutation's `onError` can call `form.setError`
 * directly; everything else is a general/business/auth/network error that
 * belongs in a toast or a `role="alert"` banner, never merged into a field.
 */
export interface ApiError {
  code: string;
  message: string;
  statusCode: number;
  fieldErrors?: Record<string, string[]>;
}

export function isApiError(value: unknown): value is ApiError {
  return typeof value === "object" && value !== null && "code" in value && "message" in value;
}

/** Entities `POST /admin/bulk/<entity>` accepts. */
export type BulkEntity =
  "songs" | "videos" | "albums" | "podcasts" | "comments" | "reports" | "users";

export interface BulkResult {
  requested: number;
  succeeded: number;
  failed: { id: number; error: string }[];
}
